import { Link } from 'react-router-dom';

import { DataTable, type Column } from '../components/DataTable.tsx';
import { Pagination } from '../components/Pagination.tsx';
import { money, num } from '../lib/format.ts';
import { usePaginatedList } from '../lib/usePaginatedList.ts';

interface Row {
  productId: string;
  name: string;
  brand: string | null;
  category: string | null;
  location: string | null;
  unit: string | null;
  price: number | null;
  onHand: number;
  committed: number;
  available: number;
}

export function InventoryPage() {
  const list = usePaginatedList<Row>({
    endpoint: '/api/dashboard/inventory',
    defaultSortColumn: 'product_id',
    defaultSortDirection: 'ASC',
    filterKeys: ['shortage'],
  });

  const columns: Column<Row>[] = [
    {
      key: 'product_id',
      label: 'SKU',
      sortable: true,
      className: 'mono',
      render: (r) => (
        <Link to={`/skus/${encodeURIComponent(r.productId)}`}>{r.productId}</Link>
      ),
    },
    { key: 'name', label: 'Name', sortable: true, render: (r) => r.name },
    { key: 'category', label: 'Category', render: (r) => r.category ?? '—' },
    { key: 'location', label: 'Loc', className: 'mono', render: (r) => r.location ?? '—' },
    { key: 'unit', label: 'Unit', className: 'mono', render: (r) => r.unit ?? '—' },
    {
      key: 'on_hand',
      label: 'On hand',
      sortable: true,
      className: 'num',
      render: (r) => num(r.onHand),
    },
    {
      key: 'committed',
      label: 'Committed',
      sortable: true,
      className: 'num',
      render: (r) => (
        <span style={{ color: r.committed > 0 ? 'var(--text)' : 'var(--text-dim)' }}>
          {num(r.committed)}
        </span>
      ),
    },
    {
      key: 'available',
      label: 'Available',
      sortable: true,
      className: 'num',
      render: (r) => (
        <span
          style={{
            color:
              r.available < 0
                ? 'var(--danger, #ff6b6b)'
                : r.available === 0
                  ? 'var(--text-muted)'
                  : 'var(--text)',
            fontWeight: r.available <= 0 ? 600 : 400,
          }}
        >
          {num(r.available)}
        </span>
      ),
    },
    {
      key: 'price',
      label: 'Price',
      className: 'num',
      render: (r) => money(r.price),
    },
  ];

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Inventory</h1>
        <span className="main-subtitle">
          {list.data ? `${list.data.total} SKUs · on hand / committed / available` : ''}
        </span>
      </header>

      <div className="toolbar">
        <input
          placeholder="Search SKU or name…"
          defaultValue={list.state.search}
          onChange={(e) => list.setSearch(e.target.value)}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={list.state.filters.shortage === '1'}
            onChange={(e) => list.setFilter('shortage', e.target.checked ? '1' : '')}
            style={{ width: 'auto', minWidth: 0 }}
          />
          <span title="Show only SKUs where on-hand minus committed is zero or negative.">
            Shortage only
          </span>
        </label>
      </div>

      {list.error && <div className="error">{(list.error as Error).message}</div>}
      {list.isLoading && !list.data && <div className="loading">Loading…</div>}

      {list.data && (
        <>
          <DataTable
            columns={columns}
            rows={list.data.rows}
            rowKey={(r) => r.productId}
            sortColumn={list.state.sortColumn}
            sortDirection={list.state.sortDirection}
            onSortChange={list.toggleSort}
          />
          <Pagination
            page={list.state.page}
            pageSize={list.state.pageSize}
            total={list.data.total}
            onPageChange={list.setPage}
          />
        </>
      )}
    </>
  );
}
